import { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Space, Typography, Card, App, Tag, Switch, Tooltip, Dropdown, Alert, Checkbox } from 'antd';
import type { MenuProps } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, RollbackOutlined, OrderedListOutlined, HolderOutlined, MoreOutlined } from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import observationFieldsService, { 
  type ObservationField, 
  type CreateObservationFieldRequest, 
  type UpdateObservationFieldRequest 
} from '../../api/observationFieldsService';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;

/** Format update_datetime - handles BigQuery format { value: string } or plain ISO string. Shows UTC. */
function formatUpdateDatetime(val: string | { value?: string } | null | undefined): string {
  const str = !val ? null : typeof val === 'string' ? val : (val as { value?: string })?.value;
  if (!str) return '-';
  const date = new Date(str);
  return isNaN(date.getTime()) ? '-' : `${date.toLocaleString(undefined, { timeZone: 'UTC', hour12: false })} UTC`;
}

interface ObservationFieldFormValues {
  name: string;
  display_name: string;
  unit?: string;
  position?: number;
  demo_only: boolean;
  hidden: boolean;
  include_in_historical_trend: boolean;
  include_in_session_compare: boolean;
}

export const ObservationFieldsManagement = () => {
  const { message } = App.useApp();
  const { authState } = useAuth();
  const token = authState.token;
  const [fields, setFields] = useState<ObservationField[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingField, setEditingField] = useState<ObservationField | null>(null);
  const [form] = Form.useForm<ObservationFieldFormValues>();

  // Reorder modal state
  const [isReorderModalOpen, setIsReorderModalOpen] = useState(false);
  const [reorderFields, setReorderFields] = useState<ObservationField[]>([]);
  const [savingOrder, setSavingOrder] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragNodeRef = useRef<HTMLDivElement | null>(null);

  // Table container ref for dynamic scroll height
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [tableScrollY, setTableScrollY] = useState<number | undefined>(undefined);

  // Include soft-deleted fields in the list (for management UI)
  const [includeDeleted, setIncludeDeleted] = useState(false);

  // Fetch all observation fields
  const fetchObservationFields = useCallback(async () => {
    setLoading(true);
    try {
      const data = await observationFieldsService.getAllObservationFields(includeDeleted);
      setFields(data);
    } catch (error) {
      message.error('Failed to fetch observation fields');
      console.error('Error fetching observation fields:', error);
    } finally {
      setLoading(false);
    }
  }, [message, includeDeleted]);

  useEffect(() => {
    fetchObservationFields();
  }, [fetchObservationFields]);

  // Measure table container and set table body scroll height (header + footer)
  const TABLE_SCROLL_RESERVE_PX = 80;
  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const updateScrollY = () => {
      if (!el.isConnected) return;
      const height = el.clientHeight;
      setTableScrollY(Math.max(200, height - TABLE_SCROLL_RESERVE_PX));
    };
    updateScrollY();
    const ro = new ResizeObserver(updateScrollY);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading, fields.length]);

  // Handle create/edit modal
  const handleOpenModal = (field?: ObservationField) => {
    if (field) {
      setEditingField(field);
      // Convert null to undefined for form compatibility
      form.setFieldsValue({
        ...field,
        unit: field.unit ?? undefined,
        position: field.position ?? undefined,
        demo_only: field.demo_only ?? false,
        hidden: field.hidden ?? false,
        include_in_historical_trend: field.include_in_historical_trend ?? false,
        include_in_session_compare: field.include_in_session_compare ?? false,
      });
    } else {
      setEditingField(null);
      form.resetFields();
      // Set default values for new field
      form.setFieldsValue({
        demo_only: false,
        hidden: false,
        include_in_historical_trend: false,
        include_in_session_compare: false,
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingField(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!token) {
        message.error('Authentication required');
        return;
      }

      setSaving(true);

      if (editingField) {
        // Update existing observation field
        const updates: UpdateObservationFieldRequest = {
          display_name: values.display_name,
          unit: values.unit || null,
          position: values.position ?? null,
          demo_only: values.demo_only,
          hidden: values.hidden,
          include_in_historical_trend: values.include_in_historical_trend,
          include_in_session_compare: values.include_in_session_compare,
        };

        await observationFieldsService.updateObservationField(editingField.name, updates, token);
        message.success('Observation field updated successfully');
      } else {
        // Create new observation field
        const fieldData: CreateObservationFieldRequest = {
          name: values.name,
          display_name: values.display_name,
          unit: values.unit || null,
          position: values.position ?? null,
          demo_only: values.demo_only ?? false,
          hidden: values.hidden ?? false,
          include_in_historical_trend: values.include_in_historical_trend ?? false,
          include_in_session_compare: values.include_in_session_compare ?? false,
        };

        await observationFieldsService.createObservationField(fieldData, token);
        message.success('Observation field created successfully');
      }

      handleCloseModal();
      fetchObservationFields();
    } catch (error) {
      console.error('Error saving observation field:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save observation field';
      message.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (name: string) => {
    try {
      if (!token) {
        message.error('Authentication required');
        return;
      }

      await observationFieldsService.deleteObservationField(name, token);
      message.success('Observation field soft-deleted successfully');
      fetchObservationFields();
    } catch (error) {
      message.error('Failed to delete observation field');
      console.error('Error deleting observation field:', error);
    }
  };

  const handleRestore = async (name: string) => {
    try {
      if (!token) {
        message.error('Authentication required');
        return;
      }

      await observationFieldsService.setObservationFieldDeleted(name, false, token);
      message.success('Observation field restored successfully');
      fetchObservationFields();
    } catch (error) {
      message.error('Failed to restore observation field');
      console.error('Error restoring observation field:', error);
    }
  };

  // Reorder modal handlers
  const handleOpenReorderModal = () => {
    // Sort non-deleted fields by current position for the reorder modal
    const activeFields = fields.filter(f => !f.deleted);
    const sorted = [...activeFields].sort((a, b) => (a.position ?? 9999) - (b.position ?? 9999));
    setReorderFields(sorted);
    setIsReorderModalOpen(true);
  };

  const handleCloseReorderModal = () => {
    setIsReorderModalOpen(false);
    setReorderFields([]);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    setDraggedIndex(index);
    dragNodeRef.current = e.currentTarget;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    // Add a slight delay to show the dragging state
    setTimeout(() => {
      if (dragNodeRef.current) {
        dragNodeRef.current.style.opacity = '0.5';
      }
    }, 0);
  };

  const handleDragEnd = () => {
    if (dragNodeRef.current) {
      dragNodeRef.current.style.opacity = '1';
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
    dragNodeRef.current = null;
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDragOverIndex(null);
      return;
    }

    const newFields = [...reorderFields];
    const [draggedItem] = newFields.splice(draggedIndex, 1);
    newFields.splice(dropIndex, 0, draggedItem);
    setReorderFields(newFields);
    setDragOverIndex(null);
  };

  const handleSaveOrder = async () => {
    if (!token) {
      message.error('Authentication required');
      return;
    }

    setSavingOrder(true);
    try {
      // Build array of fields with changed positions
      const changedFields = reorderFields
        .map((field, index) => ({
          name: field.name,
          position: index + 1,
        }))
        .filter((item, index) => reorderFields[index].position !== item.position);

      if (changedFields.length === 0) {
        message.info('No changes to save');
        handleCloseReorderModal();
        return;
      }

      // Batch update all positions in a single API call
      await observationFieldsService.reorderObservationFields(changedFields, token);
      message.success('Field order updated successfully');
      handleCloseReorderModal();
      fetchObservationFields();
    } catch (error) {
      console.error('Error saving field order:', error);
      message.error('Failed to save field order');
    } finally {
      setSavingOrder(false);
    }
  };

  const columns: ColumnsType<ObservationField> = [
    {
      title: '',
      dataIndex: 'position',
      key: 'position',
      width: 60,
      sorter: (a, b) => (a.position ?? 9999) - (b.position ?? 9999),
      defaultSortOrder: 'ascend',
      render: (position: number | null) => position !== null ? (
        <Tag color="purple" style={{ minWidth: 24, textAlign: 'center' }}>{position}</Tag>
      ) : (
        <Typography.Text type="secondary">-</Typography.Text>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name: string, record: ObservationField) => (
        <Space>
          <Text code style={{ fontSize: 12 }}>{name}</Text>
          {record.deleted && <Tag color="red">Deleted</Tag>}
        </Space>
      ),
    },
    {
      title: 'Display Name',
      dataIndex: 'display_name',
      key: 'display_name',
      sorter: (a, b) => a.display_name.localeCompare(b.display_name),
      render: (name: string) => (
        <Text style={{ fontSize: 12 }}>{name}</Text>
      ),
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
      width: 70,
      render: (unit: string | null) => (
        <Text style={{ fontSize: 12 }}>{unit || '-'}</Text>
      ),
    },
    {
      title: 'Demo',
      dataIndex: 'demo_only',
      key: 'demo_only',
      width: 100,
      sorter: (a, b) => Number(a.demo_only) - Number(b.demo_only),
      render: (demoOnly: boolean) => (
        <Tooltip title={demoOnly ? 'Only available for demo events' : 'Available for all events'}>
          <Tag color={demoOnly ? 'orange' : 'default'}>
            {demoOnly ? 'Yes' : 'No'}
          </Tag>
        </Tooltip>
      ),
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
      ],
      onFilter: (value, record) => record.demo_only === value,
    },
    {
      title: 'Hidden',
      dataIndex: 'hidden',
      key: 'hidden',
      width: 80,
      sorter: (a, b) => Number(a.hidden) - Number(b.hidden),
      render: (hidden: boolean) => (
        <Tooltip title={hidden ? 'Hidden from UI' : 'Visible in UI'}>
          <Tag color={hidden ? 'red' : 'default'}>
            {hidden ? 'Yes' : 'No'}
          </Tag>
        </Tooltip>
      ),
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
      ],
      onFilter: (value, record) => record.hidden === value,
    },
    {
      title: 'Hist. Trend',
      dataIndex: 'include_in_historical_trend',
      key: 'include_in_historical_trend',
      width: 100,
      sorter: (a, b) => Number(a.include_in_historical_trend) - Number(b.include_in_historical_trend),
      render: (included: boolean) => (
        <Tooltip title={included ? 'Shown in Historical Weather Trend view' : 'Not shown in Historical Weather Trend view'}>
          <Tag color={included ? 'green' : 'default'}>
            {included ? 'Yes' : 'No'}
          </Tag>
        </Tooltip>
      ),
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
      ],
      onFilter: (value, record) => record.include_in_historical_trend === value,
    },
    {
      title: 'Sess. Comp.',
      dataIndex: 'include_in_session_compare',
      key: 'include_in_session_compare',
      width: 100,
      sorter: (a, b) => Number(a.include_in_session_compare) - Number(b.include_in_session_compare),
      render: (included: boolean) => (
        <Tooltip title={included ? 'Shown in Session Comparison view' : 'Not shown in Session Comparison view'}>
          <Tag color={included ? 'blue' : 'default'}>
            {included ? 'Yes' : 'No'}
          </Tag>
        </Tooltip>
      ),
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
      ],
      onFilter: (value, record) => record.include_in_session_compare === value,
    },
    {
      title: 'Updated',
      key: 'update_info',
      width: 180,
      render: (_, record) => (
        <Space orientation="vertical" size={0} style={{ fontSize: 11 }}>
          {record.updated_by && (
            <Text type="secondary" style={{ fontSize: 11 }}>{record.updated_by}</Text>
          )}
          <Text type="secondary" style={{ fontSize: 10 }}>
            {formatUpdateDatetime(record.update_datetime)}
          </Text>
        </Space>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, record) => {
        const menuItems: MenuProps['items'] = [
          ...(record.deleted
            ? [
                {
                  key: 'restore',
                  icon: <RollbackOutlined />,
                  label: 'Restore',
                  onClick: () => handleRestore(record.name),
                },
              ]
            : [
                {
                  key: 'edit',
                  icon: <EditOutlined />,
                  label: 'Edit',
                  onClick: () => handleOpenModal(record),
                },
                { type: 'divider' as const },
                {
                  key: 'delete',
                  icon: <DeleteOutlined />,
                  label: 'Soft Delete',
                  danger: true,
                  onClick: () => {
                    Modal.confirm({
                      title: 'Soft Delete Observation Field',
                      content: (
                        <div>
                          Are you sure you want to soft-delete <Text code>{record.name}</Text>?
                          <br />
                          <Text type="secondary">The field can be restored later from the list.</Text>
                        </div>
                      ),
                      okText: 'Yes, Soft Delete',
                      cancelText: 'Cancel',
                      okButtonProps: { danger: true },
                      onOk: () => handleDelete(record.name),
                    });
                  },
                },
              ]),
        ];

        return (
          <Dropdown menu={{ items: menuItems }} trigger={['click']}>
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0 }}>
      <Card style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 0 }} styles={{ body: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '8px 12px' } }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <Title level={2} style={{ margin: 0 }}>Observation Fields Management</Title>
          <Space>
            <Button
              icon={<OrderedListOutlined />}
              onClick={handleOpenReorderModal}
              disabled={fields.filter(f => !f.deleted).length < 2}
            >
              Reorder
            </Button>
            <Checkbox
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
            >
              Include deleted
            </Checkbox>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => handleOpenModal()}
            >
              Add Observation Field
            </Button>
          </Space>
        </div>

        <Alert
          title="Observation Fields Configuration"
          description={
            <span>
              Configure which weather parameters are displayed from station observations.
              Use <strong>Reorder</strong> to change the display order of fields.
              <strong> Hidden</strong> fields are not shown in the observations view.
            </span>
          }
          type="info"
          showIcon
          style={{ marginBottom: 8 }}
        />

        <div ref={tableContainerRef} style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <Table
            columns={columns}
            dataSource={fields}
            rowKey="name"
            loading={loading}
            pagination={false}
            scroll={tableScrollY != null ? { y: tableScrollY } : undefined}
            size="small"
            footer={() => (
              <div style={{ textAlign: 'right', color: '#8c8c8c', fontSize: 12 }}>
                {fields.length} field{fields.length !== 1 ? 's' : ''}
              </div>
            )}
          />
        </div>
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingField ? 'Edit Observation Field' : 'Add Observation Field'}
        open={isModalOpen}
        onOk={handleSubmit}
        onCancel={handleCloseModal}
        width={480}
        okText={editingField ? 'Update' : 'Create'}
        confirmLoading={saving}
        okButtonProps={{ disabled: saving }}
        cancelButtonProps={{ disabled: saving }}
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 16 }}
        >
          {/* Row 1: Name */}
          <Form.Item
            name="name"
            label="Name"
            rules={[
              { required: true, message: 'Required' },
              { pattern: /^[a-zA-Z0-9_]+$/, message: 'Only letters, numbers, and underscores allowed' },
            ]}
            tooltip="Field identifier - matches the readings array field name"
            style={{ marginBottom: 12 }}
          >
            <Input placeholder="e.g., temperature, windSpeed" disabled={!!editingField} />
          </Form.Item>

          {/* Row 2: Display Name & Unit */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Form.Item
              name="display_name"
              label="Display Name"
              rules={[{ required: true, message: 'Required' }]}
              style={{ marginBottom: 12 }}
            >
              <Input placeholder="e.g., Air Temp" />
            </Form.Item>

            <Form.Item
              name="unit"
              label="Unit"
              tooltip="Unit of measurement (e.g., '°C', 'm/s')"
              style={{ marginBottom: 12 }}
            >
              <Input placeholder="e.g., °C, m/s, %" />
            </Form.Item>
          </div>

          {/* Row 3: Position */}
          <Form.Item
            name="position"
            label="Position"
            tooltip="Display order (lower = first)"
            style={{ marginBottom: 12, maxWidth: 120 }}
          >
            <InputNumber min={1} placeholder="1" style={{ width: '100%' }} />
          </Form.Item>

          {/* Row 4: Toggles */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Form.Item
              name="demo_only"
              label="Demo Only"
              valuePropName="checked"
              initialValue={false}
              tooltip="If enabled, this field will only be available for demo events"
              style={{ marginBottom: 12 }}
            >
              <Switch />
            </Form.Item>

            <Form.Item
              name="hidden"
              label="Hidden"
              valuePropName="checked"
              initialValue={false}
              tooltip="If enabled, this field will be hidden from the UI"
              style={{ marginBottom: 12 }}
            >
              <Switch />
            </Form.Item>

            <Form.Item
              name="include_in_historical_trend"
              label="Historical Trend"
              valuePropName="checked"
              initialValue={false}
              tooltip="If enabled, this field appears in the Historical Weather Trend view"
              style={{ marginBottom: 12 }}
            >
              <Switch />
            </Form.Item>

            <Form.Item
              name="include_in_session_compare"
              label="Session Comparison"
              valuePropName="checked"
              initialValue={false}
              tooltip="If enabled, this field appears in the Session Comparison view"
              style={{ marginBottom: 12 }}
            >
              <Switch />
            </Form.Item>
          </div>
        </Form>
      </Modal>

      {/* Reorder Modal */}
      <Modal
        title="Reorder Observation Fields"
        open={isReorderModalOpen}
        onOk={handleSaveOrder}
        onCancel={handleCloseReorderModal}
        width={500}
        okText="Save Order"
        confirmLoading={savingOrder}
        okButtonProps={{ disabled: savingOrder }}
        cancelButtonProps={{ disabled: savingOrder }}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">Drag fields to reorder them. Click "Save Order" to apply changes.</Text>
        </div>
        <div style={{ 
          maxHeight: 400, 
          overflowY: 'auto',
          border: '1px solid #d9d9d9',
          borderRadius: 8,
          padding: 8,
        }}>
          {reorderFields.map((field, index) => (
            <div
              key={field.name}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 16px',
                marginBottom: index < reorderFields.length - 1 ? 4 : 0,
                background: dragOverIndex === index ? '#e6f4ff' : '#fafafa',
                borderRadius: 6,
                cursor: 'grab',
                border: dragOverIndex === index ? '2px dashed #1890ff' : '1px solid #f0f0f0',
                transition: 'all 0.2s ease',
                userSelect: 'none',
              }}
            >
              <HolderOutlined style={{ color: '#999', fontSize: 16 }} />
              <Tag color="purple" style={{ minWidth: 28, textAlign: 'center' }}>
                {index + 1}
              </Tag>
              <div style={{ flex: 1 }}>
                <Text strong>{field.display_name}</Text>
                <Text type="secondary" style={{ marginLeft: 8 }}>({field.name})</Text>
              </div>
              {field.unit && (
                <Text type="secondary">{field.unit}</Text>
              )}
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default ObservationFieldsManagement;
